/**
 * Crisp session lifecycle — no-ops on every platform, kept so callers have one stable API.
 *
 * Haniverse no longer bundles a Crisp SDK on ANY platform. Native used to mount
 * `react-native-crisp-chat-sdk` on the Support screen; it was removed to drop the ~15MB
 * `Crisp.framework` from the iOS/Android binaries, and native now opens Crisp's hosted chatbox in
 * an in-app browser instead, from the support sheet at `/support` (built in
 * `routes/nav/app/settings/support`). Web has always reached Crisp
 * through the browser widget on the public `/support` page, which self-configures and manages its
 * own session.
 *
 * Because there is no in-app SDK left on either side, both functions are unconditionally empty and
 * the file is no longer platform-split.
 */

/**
 * No-op. The workspace id now travels with the chat itself — as the `website_id` query param on
 * the hosted chatbox URL (native) or the widget `<script>` (web) — so there is nothing to
 * configure in-process.
 */
export function configureCrispChat(): void {
  // Intentionally empty.
}

/**
 * No-op, on native as well as web.
 *
 * BEHAVIOR CHANGE (native): this used to call the native SDK's `resetSession()` on sign-out, which
 * dropped the Crisp conversation stored inside the app. The Crisp session now lives in the
 * BROWSER — the in-app browser's own cookie/storage jar for `go.crisp.chat` — which the app cannot
 * reach, so signing out can no longer clear it from the app side.
 *
 * Consequence on a shared device: after a user signs out, the next person who opens Support may
 * land back in the previous user's thread (until that browser state is cleared) rather than a
 * fresh anonymous session. Signing in again is unaffected — the Support screen passes the new
 * user's `token_id`, which switches Crisp onto that account's own conversation.
 *
 * Kept exported (rather than deleted) because the sign-out path in
 * `routes/nav/app/settings/account` calls it, and because it is the natural hook if we later clear
 * the browser session explicitly.
 */
export function resetCrispChatSession(): void {
  // Intentionally empty — see the docblock: the session is the browser's, not the app's.
}
